import { useCallback, useRef, useState } from "react";
import { onlyNumbersRegex } from "validations/matchers";
import type { IOEEFormProducts } from "../../../MachineCenterCreationFormInterfaces";
import { ProductKey, ProductListProps, VALUE_LABEL_BY_METHOD } from "./interfaces";

const getProductKey = (product: IOEEFormProducts): ProductKey => product.id ?? product.name;

const getCurrentIdealValue = (product: IOEEFormProducts): string =>
    product.product_ideal_values.length > 0 ? String(product.product_ideal_values[0].value) : "";

const isValidValue = (value: string): boolean => value === "" || (onlyNumbersRegex.test(value) && value !== "0");

export const useProductList = ({ products, setFieldValue, selectedMethodId, t }: ProductListProps) => {
    const valueLabel = selectedMethodId !== "" ? t(VALUE_LABEL_BY_METHOD[selectedMethodId]) : "";
    const nameInputRef = useRef<HTMLInputElement>(null);

    const [nameInput, setNameInput] = useState("");
    const [valueInput, setValueInput] = useState("");
    const [duplicateNameError, setDuplicateNameError] = useState(false);

    const [editingKey, setEditingKey] = useState<ProductKey | null>(null);
    const [editName, setEditName] = useState("");
    const [editValue, setEditValue] = useState("");
    const [duplicateNameEditError, setDuplicateNameEditError] = useState(false);

    const activeProducts = products.filter((p) => !p.deleted_at);

    const isDuplicateName = useCallback(
        (name: string, excludeKey?: ProductKey) => {
            return products.some(
                (p) =>
                    !p.deleted_at &&
                    getProductKey(p) !== excludeKey &&
                    p.name.toLowerCase().trim() === name.toLowerCase().trim(),
            );
        },
        [products],
    );

    const handleNameInputChange = useCallback(
        (value: string) => {
            setNameInput(value);
            setDuplicateNameError(false);
        },
        [],
    );

    const handleValueInputChange = useCallback((value: string) => {
        if (isValidValue(value)) {
            setValueInput(value);
        }
    }, []);

    const handleAdd = useCallback(() => {
        const trimmedName = nameInput.trim();
        if (!trimmedName || !valueInput) return;

        if (isDuplicateName(trimmedName)) {
            setDuplicateNameError(true);
            return;
        }
        setDuplicateNameError(false);

        const newProduct: IOEEFormProducts = {
            name: trimmedName,
            product_ideal_values: [{ value: Number(valueInput) }],
        };
        setFieldValue("oee_products", [...products, newProduct]);
        setNameInput("");
        setValueInput("");
        nameInputRef.current?.focus();
    }, [nameInput, valueInput, products, setFieldValue, isDuplicateName]);

    const handleDelete = useCallback(
        (product: IOEEFormProducts) => {
            if (product.id) {
                setFieldValue(
                    "oee_products",
                    products.map((p) => (getProductKey(p) === product.id ? { ...p, deleted_at: new Date() } : p)),
                );
            } else {
                setFieldValue(
                    "oee_products",
                    products.filter((p) => p.name !== product.name),
                );
            }
        },
        [products, setFieldValue],
    );

    const handleStartEdit = useCallback((product: IOEEFormProducts) => {
        setEditingKey(getProductKey(product));
        setEditName(product.name);
        setEditValue(getCurrentIdealValue(product));
        setDuplicateNameEditError(false);
    }, []);

    const handleEditNameChange = useCallback((value: string) => {
        setEditName(value);
        setDuplicateNameEditError(false);
    }, []);

    const handleEditValueChange = useCallback((value: string) => {
        if (isValidValue(value)) {
            setEditValue(value);
        }
    }, []);

    const handleConfirmEdit = useCallback(() => {
        const trimmedName = editName.trim();
        if (!trimmedName || !editValue || editingKey === null) return;

        if (isDuplicateName(trimmedName, editingKey)) {
            setDuplicateNameEditError(true);
            return;
        }
        setDuplicateNameEditError(false);

        setFieldValue(
            "oee_products",
            products.map((p) =>
                getProductKey(p) === editingKey
                    ? { ...p, name: trimmedName, product_ideal_values: [{ value: Number(editValue) }] }
                    : p,
            ),
        );
        setEditingKey(null);
    }, [editName, editValue, editingKey, products, setFieldValue, isDuplicateName]);

    const handleCancelEdit = useCallback(() => {
        setEditingKey(null);
        setDuplicateNameEditError(false);
    }, []);

    const canAdd = Boolean(nameInput.trim() && valueInput);

    return {
        valueLabel,
        nameInputRef,
        nameInput,
        valueInput,
        duplicateNameError,
        editingKey,
        editName,
        editValue,
        duplicateNameEditError,
        activeProducts,
        canAdd,
        handleNameInputChange,
        handleValueInputChange,
        handleAdd,
        handleDelete,
        handleStartEdit,
        handleEditNameChange,
        handleEditValueChange,
        handleConfirmEdit,
        handleCancelEdit,
        getProductKey,
        getCurrentIdealValue,
    };
};
